/**
 * Routing, and the one decision above it.
 *
 * Two faces are served from one bundle, chosen by the hostname the browser used:
 *
 *   the apex           the product site, sign-in, the workspace app, the console
 *   a tenant hostname  the screening app and that tenant's organisation console
 *
 * A tenant hostname must give nothing away. An address nobody has claimed and a
 * real workspace nobody is signed into answer identically — 404-for-unknown
 * would turn the wildcard into a way to enumerate every customer. So the
 * fallback below is the same "nothing to see here" in both cases, deliberately.
 */

import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import BrandTheme from './components/BrandTheme';
import DeadEnd from './components/DeadEnd';
import Spinner from './components/Spinner';
import { useSession } from './lib/session';

// Candidate first: it is the only route whose user cannot simply reload.
const CandidateTest = lazy(() => import('./routes/candidate/CandidateTest'));

const Landing = lazy(() => import('./routes/site/Landing'));
const SignIn = lazy(() => import('./routes/site/SignIn'));
const InvitePage = lazy(() => import('./routes/site/InvitePage'));

const Workspaces = lazy(() => import('./routes/app/Workspaces'));
const NewWorkspace = lazy(() => import('./routes/app/NewWorkspace'));
const Workspace = lazy(() => import('./routes/app/Workspace'));

const Console = lazy(() => import('./routes/console/Console'));
const People = lazy(() => import('./routes/console/People'));
const Person = lazy(() => import('./routes/console/Person'));
const Library = lazy(() => import('./routes/console/Library'));

const OrgShell = lazy(() => import('./routes/org/OrgShell'));
const OrgSignIn = lazy(() => import('./routes/org/SignIn'));
const Dashboard = lazy(() => import('./routes/org/Dashboard'));
const RolePlanning = lazy(() => import('./routes/org/RolePlanning'));
const Campaigns = lazy(() => import('./routes/org/Campaigns'));
const Campaign = lazy(() => import('./routes/org/Campaign'));
const Questionnaires = lazy(() => import('./routes/org/Questionnaires'));
const QuestionnaireEditor = lazy(() => import('./routes/org/PositionEditor'));
const Questions = lazy(() => import('./routes/org/Questions'));
const OrgTemplates = lazy(() => import('./routes/org/Templates'));
const OrgSettings = lazy(() => import('./routes/org/Settings'));
const Results = lazy(() => import('./routes/org/Results'));
const Invite = lazy(() => import('./routes/org/Invite'));
const Bulk = lazy(() => import('./routes/org/Bulk'));

/**
 * `/admin/...` is `/org/...`, permanently.
 *
 * Kept because it is not only bookmarks: the sign-in chain's own `returnTo`
 * targets pointed here, an operator's browser has autocompleted it for months,
 * and anything written down in a runbook still says `/admin`. A redirect costs
 * one component; a dead link costs a support conversation.
 */
function AdminRedirect() {
  const location = useLocation();
  const suffix = location.pathname.replace(/^\/admin/, '');
  return <Navigate to={`/org${suffix}${location.search}`} replace />;
}

/** `/org/positions/:id` was the editor's old address, and PositionEditor still
 *  reads `positionId`. Kept as a param alias rather than renamed everywhere. */
function EditorAlias() {
  const { positionId } = useParams();
  return <Navigate to={`/org/questionnaires/${positionId ?? ''}`} replace />;
}

export default function App() {
  const { identity, loading } = useSession();
  const location = useLocation();

  // The document title is set per route rather than in the HTML, because a
  // tenant hostname must not announce whose it is in a browser tab before the
  // candidate has even opened their link.
  useEffect(() => {
    if (!identity) return;
    if (identity.marketing) {
      document.title = 'candidate.observer — hire on signal, not volume';
    } else if (location.pathname.startsWith('/org') || location.pathname.startsWith('/admin')) {
      document.title = `${identity.tenant?.name ?? 'Workspace'} — screening`;
    } else {
      document.title = 'Screening';
    }
  }, [identity, location.pathname]);

  if (loading) return <Spinner full />;

  return (
    <>
      {/* A workspace's own colours, applied to the whole document. Never on the
          product site: that is one brand, and it is ours. */}
      {!identity?.marketing && <BrandTheme tokens={identity?.tenant?.branding.theme} />}

      <Suspense fallback={<Spinner full />}>
        <Routes>
          {/*
            The candidate's own route answers on a tenant hostname and needs no
            session of any kind: the link is the only credential, and it
            identifies nobody.
          */}
          <Route path="/t/:token" element={<CandidateTest />} />

          {identity?.marketing ? (
            <>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<SignIn />} />
              <Route path="/invite/:token" element={<InvitePage />} />
              <Route path="/app" element={<Workspaces />} />
              <Route path="/app/new" element={<NewWorkspace />} />
              <Route path="/app/o/:slug" element={<Workspace />} />
              <Route path="/console" element={<Console />} />
              <Route path="/console/people" element={<People />} />
              <Route path="/console/people/:accountId" element={<Person />} />
              <Route path="/console/library" element={<Library />} />
              {/* An unknown path on the product site is simply the site. */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          ) : (
            <>
              {/*
                A workspace hostname needs its own sign-in, at BOTH paths.

                `/org/login` is where somebody arrives deliberately. `/login` is
                where the *server* sends them: the silent chain answers
                `login_required` whenever Keycloak has no session to reuse —
                which happens routinely, because our own cookie lasts fourteen
                days and Keycloak's SSO session idles out in thirty minutes —
                and the callback redirects to `/login?returnTo=…` on whatever
                host the browser was on.

                Without this route that redirect landed on the catch-all below
                and the operator was told "Nothing to see here", with no way
                forward.
              */}
              <Route path="/login" element={<OrgSignIn />} />
              <Route path="/org/login" element={<OrgSignIn />} />

              <Route path="/org" element={<OrgShell />}>
                <Route index element={<Dashboard />} />
                <Route path="roles" element={<RolePlanning />} />
                <Route path="campaigns" element={<Campaigns />} />
                <Route path="campaigns/:campaignId" element={<Campaign />} />
                <Route path="questionnaires" element={<Questionnaires />} />
                <Route path="questionnaires/new" element={<QuestionnaireEditor />} />
                <Route path="questionnaires/:positionId" element={<QuestionnaireEditor />} />
                <Route path="questions" element={<Questions />} />
                <Route path="templates" element={<OrgTemplates />} />
                <Route path="settings" element={<OrgSettings />} />
                {/* Reached from a campaign and from the dashboard, not from the
                    menu: results belong to an opening, not to a destination. */}
                <Route path="results" element={<Results />} />
                <Route path="invite" element={<Invite />} />
                <Route path="bulk" element={<Bulk />} />
                {/* The editor's old address, before questionnaires had a name. */}
                <Route path="positions/:positionId" element={<EditorAlias />} />
                <Route path="positions" element={<Navigate to="/org/questionnaires" replace />} />
              </Route>

              <Route path="/admin/login" element={<OrgSignIn />} />
              <Route path="/admin/*" element={<AdminRedirect />} />
              <Route path="/admin" element={<AdminRedirect />} />

              {/*
                Everything else on a tenant hostname — including the root — says
                nothing. This is what an unclaimed address answers too.
              */}
              <Route path="*" element={<DeadEnd />} />
            </>
          )}
        </Routes>
      </Suspense>
    </>
  );
}
